import fabric from '@/lib/fabric';

function isLocalUrl(url: string): boolean {
  return (
    url.startsWith('data:') ||
    url.startsWith('blob:') ||
    url.startsWith('file:')
  );
}

function fromUrl(
  url: string,
  opts?: { crossOrigin?: string }
): Promise<fabric.Image> {
  return new Promise((resolve, reject) => {
    fabric.Image.fromURL(
      url,
      (img) => {
        if (!img || (!(img.width || 0) && !(img.height || 0))) {
          reject(new Error('Image failed to decode'));
          return;
        }
        resolve(img);
      },
      opts
    );
  });
}

/**
 * Load a URL into a Fabric image.
 * Skips crossOrigin for data/blob URLs (anonymous breaks those in Chromium).
 * Retries remote URLs without CORS if the first attempt fails.
 */
export async function loadFabricImage(url: string): Promise<fabric.Image> {
  if (isLocalUrl(url)) {
    return fromUrl(url);
  }

  try {
    return await fromUrl(url, { crossOrigin: 'anonymous' });
  } catch {
    return fromUrl(url);
  }
}

/** Fit image into a max box and center on the canvas. */
export function placeFabricImage(
  canvas: fabric.Canvas,
  img: fabric.Image,
  opts?: { maxRatio?: number }
): fabric.Image {
  const maxRatio = opts?.maxRatio ?? 0.5;
  const maxW = canvas.getWidth() * maxRatio;
  const maxH = canvas.getHeight() * maxRatio;
  const iw = img.width || 1;
  const ih = img.height || 1;
  const scale = Math.min(maxW / iw, maxH / ih, 1);

  img.set({
    left: canvas.getWidth() / 2 - (iw * scale) / 2,
    top: canvas.getHeight() / 2 - (ih * scale) / 2,
    scaleX: scale,
    scaleY: scale,
    originX: 'left',
    originY: 'top',
    selectable: true,
    evented: true,
    hasControls: true,
    hasBorders: true,
  });
  img.setCoords();
  canvas.add(img);
  canvas.setActiveObject(img);
  canvas.requestRenderAll();
  return img;
}
